import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, Plus, Trash, DownloadSimple, UploadSimple, PencilSimple } from "@phosphor-icons/react";
import { api, apiError, downloadFile } from "@/lib/api";
import EquipmentPicker from "@/components/EquipmentPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { money, num } from "./faUtils";

const clean = (v) => (v === "" || v === null || v === undefined ? "" : v);

function Field({ label, value, onChange, type = "number", testid, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[9px] uppercase font-bold text-slate-400 leading-tight block">{label}</Label>
      <Input type={type} step="any" className="h-8 text-xs" value={clean(value)}
        onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

// ------------------- Parámetros generales -------------------
function GeneralParams({ year, onChanged }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/finanalysis/params/${year}`).then((r) => setP(r.data)).catch(() => {}); }, [year]);
  if (!p) return null;
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const setW = (k, v) => setP((x) => ({ ...x, weights: { ...x.weights, [k]: v } }));
  const setB = (k, v) => setP((x) => ({ ...x, base_indices: { ...x.base_indices, [k]: v } }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/finanalysis/params/${year}`, {
        ...p,
        fx: Number(p.fx || 0), isr_rate: Number(p.isr_rate || 0), ptu_rate: Number(p.ptu_rate || 0),
        imss_factor: Number(p.imss_factor || 0),
        weights: Object.fromEntries(Object.entries(p.weights || {}).map(([k, v]) => [k, Number(v || 0)])),
        base_indices: Object.fromEntries(Object.entries(p.base_indices || {}).map(([k, v]) => [k, Number(v || 0)])),
      });
      toast.success("Parámetros guardados");
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm space-y-3" data-testid="fa-params-general">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Parámetros generales {year}</p>
        <Button size="sm" onClick={save} disabled={busy} className="gap-1.5 bg-blue-900 hover:bg-blue-800 text-white h-8" data-testid="fa-params-save-btn">
          <FloppyDisk size={13} weight="bold" /> Guardar
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <Field label="Mina / Cliente" type="text" value={p.mine_client} onChange={(v) => set("mine_client", v)} testid="fa-p-cliente" />
        <Field label="Contrato No." type="text" value={p.contract_no} onChange={(v) => set("contract_no", v)} testid="fa-p-contrato" />
        <Field label="Vigencia inicio" type="date" value={p.contract_start} onChange={(v) => set("contract_start", v)} testid="fa-p-vig-ini" />
        <Field label="Vigencia fin" type="date" value={p.contract_end} onChange={(v) => set("contract_end", v)} testid="fa-p-vig-fin" />
        <div>
          <Label className="text-[9px] uppercase font-bold text-slate-400 block">Moneda reporte</Label>
          <select className="h-8 text-xs border border-slate-200 rounded-md w-full px-2" value={p.currency}
            onChange={(e) => set("currency", e.target.value)} data-testid="fa-p-currency">
            <option value="MXN">MXN</option><option value="USD">USD</option>
          </select>
        </div>
        <Field label="TC presupuestado (MXN/USD)" value={p.fx} onChange={(v) => set("fx", v)} testid="fa-p-fx" />
        <Field label="Tasa ISR %" value={p.isr_rate} onChange={(v) => set("isr_rate", v)} testid="fa-p-isr" />
        <Field label="Tasa PTU %" value={p.ptu_rate} onChange={(v) => set("ptu_rate", v)} testid="fa-p-ptu" />
        <Field label="Factor prestaciones IMSS %" value={p.imss_factor} onChange={(v) => set("imss_factor", v)} testid="fa-p-imss" />
      </div>
      <div className="grid md:grid-cols-2 gap-4 pt-1">
        <div>
          <p className="text-[10px] uppercase font-bold text-slate-500 mb-1.5">Pesos escalatoria (deben sumar 1.00)</p>
          <div className="grid grid-cols-4 gap-2">
            {["inpc", "diesel", "acero", "usd"].map((k) => (
              <Field key={k} label={k.toUpperCase()} value={p.weights?.[k]} onChange={(v) => setW(k, v)} testid={`fa-p-w-${k}`} />
            ))}
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase font-bold text-slate-500 mb-1.5">Índices base del contrato</p>
          <div className="grid grid-cols-4 gap-2">
            {["inpc", "diesel", "acero", "usd"].map((k) => (
              <Field key={k} label={k.toUpperCase()} value={p.base_indices?.[k]} onChange={(v) => setB(k, v)} testid={`fa-p-b-${k}`} />
            ))}
          </div>
        </div>
      </div>
      <p className="text-[10px] text-slate-400 italic">
        Precio ajustado = Precio base × [w₁·(INPC/base) + w₂·(Diésel/base) + w₃·(Acero/base) + w₄·(USD/base)].
        Si un índice del mes está vacío, ese componente queda en 1.0 (sin escalatoria).
      </p>
    </div>
  );
}

// ------------------- Frentes -------------------
function Frentes({ year, canWrite, onChanged }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", seccion: "", terreno: "", precio_ml: "" });
  const [editingId, setEditingId] = useState(null);
  const fileRef = useRef(null);
  const load = () => api.get("/finanalysis/frentes").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.warning("Nombre del frente requerido"); return; }
    const payload = { ...form, precio_ml: Number(form.precio_ml || 0), active: form.active !== false };
    try {
      if (editingId) await api.put(`/finanalysis/frentes/${editingId}`, payload);
      else await api.post("/finanalysis/frentes", payload);
      toast.success("Frente guardado");
      setForm({ name: "", seccion: "", terreno: "", precio_ml: "" }); setEditingId(null);
      load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/finanalysis/frentes/${id}`); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const importFile = async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/finanalysis/import/${year}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importado: ${r.data.frentes_creados} frentes nuevos, ${r.data.frentes_actualizados} actualizados, meses: ${r.data.months_updated.join(", ") || "—"}`);
      if (r.data.errors?.length) toast.warning(r.data.errors.slice(0, 3).join(" · "));
      load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm space-y-3" data-testid="fa-frentes">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Catálogo de frentes · precio base por ML (sección/terreno)</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-8 gap-1.5"
            onClick={() => downloadFile(`/finanalysis/template/${year}`, `plantilla_budget_ml_${year}.xlsx`)}
            data-testid="fa-frentes-template-btn">
            <DownloadSimple size={13} weight="bold" /> Plantilla Excel
          </Button>
          <Button variant="outline" size="sm" className="h-8 gap-1.5"
            onClick={() => downloadFile("/finanalysis/template/frentes", "plantilla_frentes.xlsx")}
            data-testid="fa-frentes-catalog-template-btn">
            <DownloadSimple size={13} weight="bold" /> Solo catálogo
          </Button>
          {canWrite && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden"
                onChange={(e) => { if (e.target.files?.[0]) importFile(e.target.files[0]); e.target.value = ""; }} />
              <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => fileRef.current?.click()} data-testid="fa-frentes-import-btn">
                <UploadSimple size={13} weight="bold" /> Importar Excel
              </Button>
            </>
          )}
        </div>
      </div>
      {canWrite && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
          <Field label="Frente" type="text" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="fa-frente-name" />
          <Field label="Sección (ej. 4x4)" type="text" value={form.seccion} onChange={(v) => setForm({ ...form, seccion: v })} testid="fa-frente-seccion" />
          <Field label="Tipo terreno" type="text" value={form.terreno} onChange={(v) => setForm({ ...form, terreno: v })} testid="fa-frente-terreno" />
          <Field label="Precio ML ($)" value={form.precio_ml} onChange={(v) => setForm({ ...form, precio_ml: v })} testid="fa-frente-precio" />
          <Button size="sm" onClick={save} className="h-8 gap-1 bg-blue-900 hover:bg-blue-800 text-white" data-testid="fa-frente-save-btn">
            <Plus size={13} weight="bold" /> {editingId ? "Actualizar" : "Agregar"}
          </Button>
        </div>
      )}
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-200">
          <th className="py-1.5">Frente</th><th>Sección</th><th>Terreno</th><th className="text-right">Precio ML</th>
          <th className="text-center">Activo</th>{canWrite && <th />}
        </tr></thead>
        <tbody>
          {list.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-slate-400 text-xs italic">Sin frentes. Agrega uno o importa la plantilla Excel.</td></tr>}
          {list.map((f) => (
            <tr key={f.id} className="border-b border-slate-50" data-testid={`fa-frente-row-${f.id}`}>
              <td className="py-1.5 font-semibold text-slate-900">{f.name}</td>
              <td className="text-slate-500">{f.seccion}</td>
              <td className="text-slate-500">{f.terreno}</td>
              <td className="text-right tabular-nums">{money(f.precio_ml)}</td>
              <td className="text-center">
                <Switch checked={f.active !== false} disabled={!canWrite}
                  onCheckedChange={async (v) => {
                    try { await api.put(`/finanalysis/frentes/${f.id}`, { ...f, active: v }); load(); onChanged?.(); }
                    catch (e) { toast.error(apiError(e)); }
                  }} />
              </td>
              {canWrite && (
                <td className="text-right">
                  <button className="text-slate-400 hover:text-blue-800 p-1" onClick={() => { setForm(f); setEditingId(f.id); }} aria-label="Editar">
                    <PencilSimple size={14} weight="bold" />
                  </button>
                  <button className="text-slate-400 hover:text-red-600 p-1" onClick={() => remove(f.id)} aria-label="Borrar" data-testid={`fa-frente-del-${f.id}`}>
                    <Trash size={14} weight="bold" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ------------------- Equipos CAPEX -------------------
const emptyEq = () => ({ name: "", valor: "", mes_compra: "", vida_util_meses: "60", valor_rescate: "",
  credito: { monto: "", tasa_anual_pct: "", plazo_meses: "" } });

function Equipos({ canWrite, onChanged }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(emptyEq());
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/finanalysis/equipment").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.warning("Nombre del equipo requerido"); return; }
    const payload = {
      name: form.name, valor: Number(form.valor || 0), mes_compra: form.mes_compra,
      vida_util_meses: Number(form.vida_util_meses || 0), valor_rescate: Number(form.valor_rescate || 0),
      credito: {
        monto: Number(form.credito?.monto || 0),
        tasa_anual_pct: Number(form.credito?.tasa_anual_pct || 0),
        plazo_meses: Number(form.credito?.plazo_meses || 0),
      },
    };
    try {
      if (editingId) await api.put(`/finanalysis/equipment/${editingId}`, payload);
      else await api.post("/finanalysis/equipment", payload);
      toast.success("Equipo guardado");
      setForm(emptyEq()); setEditingId(null); load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/finanalysis/equipment/${id}`); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setC = (k, v) => setForm((f) => ({ ...f, credito: { ...f.credito, [k]: v } }));

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm space-y-3" data-testid="fa-equipos">
      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
        CAPEX y equipos · depreciación lineal + crédito (amortización lineal de capital)
      </p>
      {canWrite && (
        <div className="grid grid-cols-2 md:grid-cols-8 gap-2 items-end">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Equipo (del catálogo de mantenimiento)</label>
            <EquipmentPicker
              mode="label"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              dataTestId="fa-eq-name"
              placeholder="Selecciona equipo…"
            />
          </div>
          <Field label="Valor adquisición" value={form.valor} onChange={(v) => setForm({ ...form, valor: v })} testid="fa-eq-valor" />
          <Field label="Mes compra" type="month" value={form.mes_compra} onChange={(v) => setForm({ ...form, mes_compra: v })} testid="fa-eq-mes" />
          <Field label="Vida útil (meses)" value={form.vida_util_meses} onChange={(v) => setForm({ ...form, vida_util_meses: v })} testid="fa-eq-vida" />
          <Field label="Valor rescate" value={form.valor_rescate} onChange={(v) => setForm({ ...form, valor_rescate: v })} testid="fa-eq-rescate" />
          <div className="grid grid-cols-3 gap-1 md:col-span-2">
            <Field label="Crédito $" value={form.credito?.monto} onChange={(v) => setC("monto", v)} testid="fa-eq-cred-monto" />
            <Field label="Tasa % anual" value={form.credito?.tasa_anual_pct} onChange={(v) => setC("tasa_anual_pct", v)} testid="fa-eq-cred-tasa" />
            <Field label="Plazo (meses)" value={form.credito?.plazo_meses} onChange={(v) => setC("plazo_meses", v)} testid="fa-eq-cred-plazo" />
          </div>
          <Button size="sm" onClick={save} className="h-8 gap-1 bg-blue-900 hover:bg-blue-800 text-white" data-testid="fa-eq-save-btn">
            <Plus size={13} weight="bold" /> {editingId ? "Actualizar" : "Agregar"}
          </Button>
        </div>
      )}
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-200">
          <th className="py-1.5">Equipo</th><th className="text-right">Valor</th><th>Compra</th>
          <th className="text-right">Vida</th><th className="text-right">Dep./mes</th>
          <th className="text-right">Crédito</th><th className="text-right">Pago cap./mes</th>{canWrite && <th />}
        </tr></thead>
        <tbody>
          {list.length === 0 && <tr><td colSpan={8} className="py-4 text-center text-slate-400 text-xs italic">Sin equipos registrados.</td></tr>}
          {list.map((e) => {
            const dep = e.vida_util_meses > 0 ? (e.valor - (e.valor_rescate || 0)) / e.vida_util_meses : 0;
            const cap = e.credito?.plazo_meses > 0 ? e.credito.monto / e.credito.plazo_meses : 0;
            return (
              <tr key={e.id} className="border-b border-slate-50" data-testid={`fa-eq-row-${e.id}`}>
                <td className="py-1.5 font-semibold text-slate-900">{e.name}</td>
                <td className="text-right tabular-nums">{money(e.valor)}</td>
                <td className="text-slate-500">{e.mes_compra}</td>
                <td className="text-right">{num(e.vida_util_meses, 0)} m</td>
                <td className="text-right tabular-nums text-slate-600">{money(dep)}</td>
                <td className="text-right tabular-nums">{e.credito?.monto > 0 ? `${money(e.credito.monto)} · ${e.credito.tasa_anual_pct}% · ${e.credito.plazo_meses}m` : "—"}</td>
                <td className="text-right tabular-nums text-slate-600">{cap > 0 ? money(cap) : "—"}</td>
                {canWrite && (
                  <td className="text-right">
                    <button className="text-slate-400 hover:text-blue-800 p-1"
                      onClick={() => { setForm({ ...e, credito: e.credito || emptyEq().credito }); setEditingId(e.id); }} aria-label="Editar">
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button className="text-slate-400 hover:text-red-600 p-1" onClick={() => remove(e.id)} aria-label="Borrar" data-testid={`fa-eq-del-${e.id}`}>
                      <Trash size={14} weight="bold" />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function FaParams({ year, canWrite, onChanged }) {
  return (
    <div className="space-y-4 pt-3">
      {canWrite && <GeneralParams year={year} onChanged={onChanged} />}
      <Frentes year={year} canWrite={canWrite} onChanged={onChanged} />
      <Equipos canWrite={canWrite} onChanged={onChanged} />
    </div>
  );
}
